import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  CheckCircle2, AlertTriangle, XCircle, HelpCircle, ChevronDown, RefreshCcw, Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CheckStatus, DomainFinding, DomainHealth } from '@/hooks/useSenderDomainHealth';

/**
 * Sender domain authentication panel (M1, #752).
 *
 * WE FLAG, WE DO NOT PRESCRIBE. Every string here comes from the check engine
 * and describes what was OBSERVED. There is no "how to fix" -- the "?" explains
 * why a check matters and links to a help article, and the article hands off to
 * an administrator. See the checks.ts header for why.
 *
 * `detail` interpolates third-party DNS content and is rendered as TEXT. Never
 * introduce dangerouslySetInnerHTML here.
 *
 * Distinct from components/enterprise/SenderHealthPanel, an unrelated feature.
 */

const STATUS_UI: Record<CheckStatus, {
  Icon: typeof CheckCircle2; tone: string; label: string; badge: string;
}> = {
  pass: {
    Icon: CheckCircle2,
    tone: 'text-emerald-600',
    label: 'Looks good',
    badge: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
  },
  warn: {
    Icon: AlertTriangle,
    tone: 'text-amber-600',
    label: 'Worth checking',
    badge: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
  },
  fail: {
    Icon: XCircle,
    tone: 'text-red-600',
    label: 'Needs attention',
    badge: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300',
  },
  // Deliberately NOT styled as a failure. "We could not check" is not "something
  // is wrong", and conflating the two is what makes a panel untrustworthy.
  unknown: {
    Icon: HelpCircle,
    tone: 'text-muted-foreground',
    label: "Couldn't check",
    badge: 'border-border bg-muted text-muted-foreground',
  },
};

/**
 * Summary + tone are derived from the FINDINGS, not from `overall`.
 *
 * `overall` is an engine-internal severity gate that deliberately excludes
 * unprovable checks from the headline. Using it here produced two contradictions
 * (#819 F1/F2): a domain with zero failures read "4 checks need attention"
 * (that phrase is the FAIL label, while those rows say "Worth checking"), and an
 * emerald badge could sit over amber rows saying "worth a look" -- where colour
 * wins and the words lose.
 */
function summarise(findings: DomainFinding[]): { text: string; tone: CheckStatus } {
  const fails = findings.filter((f) => f.status === 'fail').length;
  const warns = findings.filter((f) => f.status === 'warn').length;
  if (!fails && !warns) return { text: 'All checks passed', tone: 'pass' };
  if (!fails) return { text: `${warns} ${warns === 1 ? 'check' : 'checks'} worth a look`, tone: 'warn' };
  return { text: `${fails} ${fails === 1 ? 'check needs' : 'checks need'} attention`, tone: 'fail' };
}

/** Problems first. Fixed engine order buried them mid-list (#819 U1). */
const ROW_ORDER: Record<CheckStatus, number> = { fail: 0, warn: 1, unknown: 2, pass: 3 };

function relativeTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (!Number.isFinite(mins) || mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function FindingRow({ finding }: { finding: DomainFinding }) {
  const ui = STATUS_UI[finding.status];
  return (
    <li className="flex items-start gap-2.5 py-2">
      <ui.Icon className={cn('h-4 w-4 mt-0.5 shrink-0', ui.tone)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium">{finding.title}</span>
          {/* Status is conveyed by icon colour, so it needs a text equivalent. */}
          <span className="sr-only">{ui.label}.</span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                // 24x24 minimum hit target -- WCAG 2.2 AA 2.5.8. The icon stays
                // small; the button grows into the row's existing padding.
                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`What does "${finding.title}" mean?`}
              >
                <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 text-sm">
              <p className="font-medium mb-1.5">{finding.title}</p>
              <p className="text-muted-foreground">{finding.whyItMatters}</p>
              <Link
                to={`/help/email-campaigns/${finding.helpArticleSlug}`}
                className="mt-3 inline-block text-primary hover:underline"
              >
                Read more about this
              </Link>
            </PopoverContent>
          </Popover>
        </div>
        {/* Plain text by design -- interpolates third-party DNS content. */}
        <p className="text-sm text-muted-foreground mt-0.5">{finding.detail}</p>
      </div>
    </li>
  );
}

interface Props {
  domain: string;
  health?: DomainHealth;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  onRecheck: (domain: string) => void;
}

export function SenderDomainHealthPanel({
  domain, health, loading, refreshing, error, onRecheck,
}: Props) {
  const [open, setOpen] = useState(false);

  if (loading && !health) return <Skeleton className="h-9 w-full" />;

  // "No panel" is indistinguishable from "no problems" -- the exact confusion
  // this feature exists to remove -- so an absent or failed result still
  // renders a row saying so (#819 F6/F7).
  const summary = health
    ? summarise(health.findings)
    : { text: error ? "Couldn't check" : 'Not checked yet', tone: 'unknown' as CheckStatus };
  const ui = STATUS_UI[summary.tone];
  const rows = health
    ? [...health.findings].sort((a, b) => ROW_ORDER[a.status] - ROW_ORDER[b.status])
    : [];

  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          disabled={!health}
          className="flex min-w-0 items-center gap-2 rounded text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default"
        >
          {health && (
            <ChevronDown
              className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
              aria-hidden="true"
            />
          )}
          <span className="truncate text-muted-foreground">Email setup for {domain}</span>
          {/* span, not the Badge component: Badge renders a div, and a button's
              content model is phrasing content only (#819 U7). */}
          <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-xs font-normal', ui.badge)}>
            {summary.text}
          </span>
        </button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => { if (!refreshing) onRecheck(domain); }}
          // NOT `disabled` -- disabling a focused element drops focus to
          // <body>, silently teleporting a keyboard user to the top of the
          // page mid-action (#819 F4).
          aria-busy={refreshing}
          className="h-7 shrink-0 text-xs text-muted-foreground"
        >
          {refreshing
            ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />Checking…</>
            : <><RefreshCcw className="mr-1 h-3 w-3" aria-hidden="true" />Re-check</>}
        </Button>
      </div>

      {error && !health && (
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
      )}

      {/* Results change under the user after a re-check, so announce them. */}
      <div aria-live="polite">
        {open && health && (
          <div className="mt-1 pl-6">
            <ul className="divide-y">
              {rows.map((f) => <FindingRow key={f.id} finding={f} />)}
            </ul>
            <p className="mt-2 text-xs text-muted-foreground">
              Checked {relativeTime(health.checkedAt)}. We only read your public DNS records — nothing here changes your email setup.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
